import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, Search, Home, Users, Phone, CreditCard, ChevronRight, ChevronLeft, Tag } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { productService, categoryService } from '../../services/api';
import logo from '../../assets/logo.png';

const Header = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showCategoriesMenu, setShowCategoriesMenu] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const { getCartCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef(null);
  const mobileSearchRef = useRef(null);
  const debounceRef = useRef(null);
  const categoriesRef = useRef(null);

  // Verificar si se puede hacer scroll en las categorías
  const checkCategoriesScroll = () => {
    if (categoriesRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = categoriesRef.current;
      const hasOverflow = scrollWidth > clientWidth;
      setCanScrollLeft(hasOverflow && scrollLeft > 5);
      setCanScrollRight(hasOverflow && scrollLeft < scrollWidth - clientWidth - 5);
    }
  };

  // Detectar scroll en categorías y resize
  useEffect(() => {
    const container = categoriesRef.current;
    if (!container) return;

    // Esperar a que el contenedor tenga contenido
    const timer = setTimeout(() => {
      checkCategoriesScroll();
    }, 100);

    container.addEventListener('scroll', checkCategoriesScroll, { passive: true });
    window.addEventListener('resize', checkCategoriesScroll);

    return () => {
      clearTimeout(timer);
      container.removeEventListener('scroll', checkCategoriesScroll);
      window.removeEventListener('resize', checkCategoriesScroll);
    };
  }, [categories]);

  // Scroll de categorías
  const scrollCategories = (direction) => {
    if (categoriesRef.current) {
      const scrollAmount = 200;
      categoriesRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const UPLOADS_URL = import.meta.env.VITE_UPLOADS_URL;

  // Cargar categorías
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await categoryService.getAll();
        const categoriesData = response.data.data || response.data || [];
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    loadCategories();
  }, []);

  // Cerrar sugerencias al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current && !searchRef.current.contains(event.target) &&
        mobileSearchRef.current && !mobileSearchRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Buscar sugerencias con debounce
  const searchSuggestions = async (query) => {
    if (query.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setLoadingSuggestions(true);
    try {
      const response = await productService.getAll({ search: query, limit: 6 });
      const products = response.data.data || response.data || [];
      setSuggestions(products.slice(0, 6));
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error searching suggestions:', error);
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    // Debounce de 300ms
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      searchSuggestions(value);
    }, 300);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setShowSuggestions(false);
      navigate(`/?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  const handleSuggestionClick = (productId) => {
    setShowSuggestions(false);
    setSearchQuery('');
    navigate(`/producto/${productId}`);
  };

  const getImageUrl = (product) => {
    const imgPath = product.primary_image_url || product.primary_image;
    if (!imgPath) return null;
    if (imgPath.startsWith('http')) return imgPath;
    if (imgPath.startsWith('/ecommerce-api')) {
      return `https://decohomesinrival.com.ar${imgPath}`;
    }
    return `${UPLOADS_URL}${imgPath}`;
  };

  const formatPrice = (price) => {
    const number = Math.round(Number(price) || 0);
    return '$' + number.toLocaleString('de-DE');
  };

  // Componente de sugerencias reutilizable
  const SuggestionsDropdown = () => {
    if (!showSuggestions) return null;

    return (
      <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-50">
        {loadingSuggestions ? (
          <div className="p-4 text-center text-gray-500">
            <div className="w-5 h-5 border-2 border-[#2d3c5d] border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        ) : suggestions.length > 0 ? (
          <>
            {suggestions.map((product) => (
              <button
                key={product.id}
                onClick={() => handleSuggestionClick(product.id)}
                className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition text-left border-b border-gray-100 last:border-b-0"
              >
                {/* Imagen del producto */}
                <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                  {getImageUrl(product) ? (
                    <img
                      src={getImageUrl(product)}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Search className="w-5 h-5 text-gray-300" />
                    </div>
                  )}
                </div>
                {/* Info del producto */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {product.name}
                  </p>
                  <p className="text-sm text-[#2d3c5d] font-semibold">
                    {formatPrice(product.sale_price || product.price)}
                  </p>
                </div>
              </button>
            ))}
            {/* Ver todos los resultados */}
            <button
              onClick={handleSearch}
              className="w-full p-3 text-center text-sm font-medium text-[#2d3c5d] hover:bg-[#eddacb]/20 transition border-t border-gray-200"
            >
              Ver todos los resultados para "{searchQuery}"
            </button>
          </>
        ) : searchQuery.trim().length >= 2 ? (
          <div className="p-4 text-center text-gray-500 text-sm">
            No se encontraron productos
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <>
      {/* Header principal + Barra de categorías */}
      <header className="bg-[#2d3c5d] shadow-lg sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            {/* Logo - link al inicio o scroll arriba si ya estamos en home */}
            <button
              onClick={() => {
                if (location.pathname === '/') {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                  navigate('/');
                }
              }}
              className="flex items-center flex-shrink-0 cursor-pointer"
              title="Ir al inicio"
            >
              <img src={logo} alt="Deco Home - Ir al inicio" className="h-16 md:h-20 lg:h-24 w-auto filter brightness-110" />
            </button>

            {/* Barra de búsqueda - Desktop */}
            <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-2xl mx-8" ref={searchRef}>
              <div className="relative w-full">
                <input
                  type="text"
                  placeholder="¿Qué estás buscando?"
                  value={searchQuery}
                  onChange={handleInputChange}
                  onFocus={() => searchQuery.trim().length >= 2 && setShowSuggestions(true)}
                  className="w-full px-4 py-3 pr-12 rounded-full bg-white text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#eddacb]"
                  autoComplete="off"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-[#2d3c5d] hover:bg-[#1f2a3d] transition"
                >
                  <Search className="w-5 h-5 text-white" />
                </button>
                <SuggestionsDropdown />
              </div>
            </form>

            {/* MODO CATÁLOGO: Carrito desktop oculto */}
            {/* <div className="hidden md:flex items-center gap-4">
              <Link
                to="/carrito"
                className="relative flex items-center justify-center w-12 h-12 bg-white rounded-full hover:bg-[#eddacb] transition"
              >
                <ShoppingCart className="w-6 h-6 text-[#2d3c5d]" />
                {getCartCount() > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {getCartCount()}
                  </span>
                )}
              </Link>
            </div> */}

            {/* Botón menú móvil */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="md:hidden text-white p-2"
            >
              {showMobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Barra de búsqueda - Mobile */}
          <form onSubmit={handleSearch} className="md:hidden mt-4" ref={mobileSearchRef}>
            <div className="relative">
              <input
                type="text"
                placeholder="¿Qué estás buscando?"
                value={searchQuery}
                onChange={handleInputChange}
                onFocus={() => searchQuery.trim().length >= 2 && setShowSuggestions(true)}
                className="w-full px-4 py-2 pr-12 rounded-full bg-white text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#eddacb]"
                autoComplete="off"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-[#2d3c5d]"
              >
                <Search className="w-4 h-4 text-white" />
              </button>
              <SuggestionsDropdown />
            </div>
          </form>

        </div>

        {/* Menú móvil */}
        {showMobileMenu && (
          <div className="md:hidden bg-[#1f2a3d] border-t border-white/10">
            <nav className="container mx-auto px-4 py-4 space-y-1">
              <Link
                to="/"
                className="flex items-center gap-3 text-white hover:text-[#eddacb] hover:bg-white/5 transition py-3 px-2 rounded-lg"
                onClick={() => setShowMobileMenu(false)}
              >
                <Home className="w-5 h-5" />
                Inicio
              </Link>

              {/* Categorías desplegable */}
              <div>
                <button
                  onClick={() => setShowCategoriesMenu(!showCategoriesMenu)}
                  className="w-full flex items-center justify-between text-white hover:text-[#eddacb] hover:bg-white/5 transition py-3 px-2 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Tag className="w-5 h-5" />
                    Categorías
                  </div>
                  <ChevronRight className={`w-5 h-5 transition-transform ${showCategoriesMenu ? 'rotate-90' : ''}`} />
                </button>

                {/* Lista de categorías */}
                {showCategoriesMenu && (
                  <div className="ml-4 mt-1 space-y-1 border-l-2 border-white/20 pl-4">
                    <Link
                      to="/productos"
                      className="block text-white/80 hover:text-[#eddacb] transition py-2 px-2 rounded text-sm"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      Ver todos
                    </Link>
                    {categories.map((category) => (
                      <Link
                        key={category.id}
                        to={`/productos?category=${category.id}`}
                        className="block text-white/80 hover:text-[#eddacb] transition py-2 px-2 rounded text-sm"
                        onClick={() => setShowMobileMenu(false)}
                      >
                        {category.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/nosotros"
                className="flex items-center gap-3 text-white hover:text-[#eddacb] hover:bg-white/5 transition py-3 px-2 rounded-lg"
                onClick={() => setShowMobileMenu(false)}
              >
                <Users className="w-5 h-5" />
                Quiénes Somos
              </Link>
              <Link
                to="/contacto"
                className="flex items-center gap-3 text-white hover:text-[#eddacb] hover:bg-white/5 transition py-3 px-2 rounded-lg"
                onClick={() => setShowMobileMenu(false)}
              >
                <Phone className="w-5 h-5" />
                Contacto
              </Link>
              <Link
                to="/medios-de-pago"
                className="flex items-center gap-3 text-white hover:text-[#eddacb] hover:bg-white/5 transition py-3 px-2 rounded-lg"
                onClick={() => setShowMobileMenu(false)}
              >
                <CreditCard className="w-5 h-5" />
                Medios de Pago
              </Link>

              {/* MODO CATÁLOGO: Carrito móvil oculto */}
              {/* <div className="border-t border-white/10 my-2"></div>
              <Link
                to="/carrito"
                className="flex items-center gap-3 text-white hover:text-[#eddacb] hover:bg-white/5 transition py-3 px-2 rounded-lg"
                onClick={() => setShowMobileMenu(false)}
              >
                <ShoppingCart className="w-5 h-5" />
                Carrito ({getCartCount()})
              </Link> */}
            </nav>
          </div>
        )}

        {/* Barra de Categorías - Solo Desktop - Dentro del Header */}
        <div className="hidden md:block bg-[#1f2a3d] border-t border-white/10 py-1.5">
          <div className="container mx-auto px-4">
            <div className="flex items-center gap-2">
              {/* Flecha izquierda - siempre renderizada, opacity controla visibilidad */}
              <button
                onClick={() => scrollCategories('left')}
                className={`flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-opacity duration-200 ${
                  canScrollLeft ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
                aria-label="Scroll izquierda"
              >
                <ChevronLeft className="w-4 h-4 text-white" />
              </button>

              {/* Contenedor de categorías */}
              <div
                ref={categoriesRef}
                className="flex-1 overflow-x-auto scroll-smooth scrollbar-hide"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
              >
                <div className="flex items-center gap-1 whitespace-nowrap">
                  {categories.map((category) => (
                    <Link
                      key={category.id}
                      to={`/productos?category=${category.id}`}
                      className="inline-block font-medium text-sm text-white/70 hover:text-white transition-colors duration-200 whitespace-nowrap py-1 px-3"
                    >
                      {category.name}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Flecha derecha - siempre renderizada, opacity controla visibilidad */}
              <button
                onClick={() => scrollCategories('right')}
                className={`flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-opacity duration-200 ${
                  canScrollRight ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
                aria-label="Scroll derecha"
              >
                <ChevronRight className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
